import React, { useState, useEffect } from "react";
import { useCreateSource, useUpdateSource } from "../../hooks/useApi";
import { useTranslation } from "../../hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ValidationMessage from "../forms/ValidationMessage";
import { parseApiError } from "../../utils/validation";
import { HelpCircle } from "lucide-react";

/**
 * Dialog to create OR edit a FAQ source: ONE question + ONE answer (no title).
 * Each FAQ is its own source. Only MANUAL FAQ (faq_origin === "manual") are
 * editable — FAQ detected from a crawled URL are read-only (no edit dialog opened).
 */
const FaqSourceDialog = ({
  isOpen,
  onOpenChange,
  botId,
  onSuccess,
  editSource = null,
}) => {
  const createSourceMutation = useCreateSource();
  const updateSourceMutation = useUpdateSource();
  const { t } = useTranslation();

  const isEditMode = Boolean(editSource);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [formError, setFormError] = useState("");

  // Prefill when editing, clear when creating
  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      setQuestion(editSource.question || "");
      setAnswer(editSource.answer || "");
    } else {
      setQuestion("");
      setAnswer("");
    }
    setFormError("");
  }, [isOpen, isEditMode, editSource]);

  const isSubmitting = isEditMode
    ? updateSourceMutation.isPending
    : createSourceMutation.isPending;
  const MIN_FAQ_CHARS = 50;
  const charCount = question.trim().length + answer.trim().length;
  const tooShort = charCount < MIN_FAQ_CHARS;

  const handleOpenChange = (open) => {
    if (!open) {
      setQuestion("");
      setAnswer("");
      setFormError("");
    }
    onOpenChange(open);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const q = question.trim();
    const a = answer.trim();
    if (!q || !a) {
      setFormError(
        t("dialogs.faq.errorEmpty", "Domanda e risposta sono obbligatorie.")
      );
      return;
    }
    if (q.length + a.length < MIN_FAQ_CHARS) {
      setFormError(
        t(
          "dialogs.faq.errorTooShort",
          "La FAQ (domanda + risposta) deve avere almeno 50 caratteri."
        )
      );
      return;
    }
    try {
      if (isEditMode) {
        await updateSourceMutation.mutateAsync({
          botId,
          sourceId: editSource._id,
          data: { question: q, answer: a },
        });
      } else {
        await createSourceMutation.mutateAsync({
          botId,
          data: { type: "faq", question: q, answer: a },
        });
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(
        isEditMode ? "Failed to update FAQ source:" : "Failed to create FAQ source:",
        error
      );
      setFormError(parseApiError(error).message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {isEditMode
              ? t("dialogs.faq.titleEdit", "Modifica FAQ")
              : t("dialogs.faq.titleAdd", "Aggiungi FAQ")}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t(
                  "dialogs.faq.descEdit",
                  "Aggiorna domanda e risposta. La modifica verrà re-indicizzata nella knowledge base."
                )
              : t(
                  "dialogs.faq.desc",
                  "Una domanda e la sua risposta. Diventa una fonte a sé, ricercabile dal bot."
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="faq-question" className="block text-sm font-medium">
              {t("dialogs.faq.questionLabel", "Domanda")}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="faq-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t(
                "dialogs.faq.questionPlaceholder",
                "Es. Quali sono gli orari di apertura?"
              )}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="faq-answer" className="block text-sm font-medium">
              {t("dialogs.faq.answerLabel", "Risposta")}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <textarea
              id="faq-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t(
                "dialogs.faq.answerPlaceholder",
                "Es. Siamo aperti dal lunedì al venerdì, 9:00–18:00."
              )}
              className="w-full h-40 p-3 border border-input rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {formError && (
            <ValidationMessage
              type="error"
              message={formError}
              showIcon={true}
              size="md"
            />
          )}

          <p
            className={`text-xs ${
              tooShort ? "text-amber-600" : "text-muted-foreground"
            }`}
          >
            {charCount}/{MIN_FAQ_CHARS}{" "}
            {t(
              "dialogs.faq.minCharsHint",
              "caratteri minimi (domanda + risposta)"
            )}
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel", "Annulla")}
            </Button>
            <Button type="submit" disabled={isSubmitting || tooShort}>
              {isEditMode
                ? isSubmitting
                  ? t("dialogs.faq.saving", "Salvataggio...")
                  : t("dialogs.faq.saveChanges", "Salva modifiche")
                : isSubmitting
                ? t("dialogs.faq.creating", "Creazione...")
                : t("dialogs.faq.addSource", "Aggiungi FAQ")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FaqSourceDialog;
